import { type ApiUser } from '../services/api';
import { escapeHtml } from './puzzleScreen';

export const MIN_PASSWORD_LENGTH = 8;

export interface AccountView {
  readonly user: ApiUser | null;
  readonly online: boolean;
}

function credentialFields(
  prefix: string,
  autocomplete: 'new-password' | 'current-password',
): string {
  return `
    <label class="field">
      <span>Email</span>
      <input type="email" name="email" id="${prefix}-email" required autocomplete="email" maxlength="254" />
    </label>
    <label class="field">
      <span>Password</span>
      <input type="password" name="password" id="${prefix}-password" required
             autocomplete="${autocomplete}" minlength="${MIN_PASSWORD_LENGTH}" maxlength="200" />
    </label>
    <p class="form__error" data-role="error" role="alert" hidden></p>
  `;
}

function guestMarkup(): string {
  return `
    <article class="note note--order account__note">
      <span class="note__staple" aria-hidden="true"></span>
      <p class="note__kicker">Status · Guest runner</p>
      <h2 class="note__title">Unregistered</h2>
      <p class="note__body">
        Your runs are kept on this device and in a guest account on the server. Register to keep
        them for good and to sign in from another device.
      </p>
    </article>
    <form class="form" data-form="register" novalidate>
      <h2 class="form__title">Register this runner</h2>
      ${credentialFields('register', 'new-password')}
      <p class="form__hint">At least ${MIN_PASSWORD_LENGTH} characters. There is no password reset yet, so keep it somewhere safe.</p>
      <div class="brief__actions">
        <button type="submit" class="btn btn--primary">Create account</button>
      </div>
    </form>
    <form class="form" data-form="signin" novalidate>
      <h2 class="form__title">Already registered?</h2>
      <p class="form__hint">Sign in and everything played on this device joins your account.</p>
      ${credentialFields('signin', 'current-password')}
      <div class="brief__actions">
        <button type="submit" class="btn btn--ghost">Sign in</button>
      </div>
    </form>
  `;
}

function signedInMarkup(user: ApiUser): string {
  const since = new Date(user.createdAt);
  const sinceLabel = Number.isNaN(since.getTime())
    ? ''
    : ` · runner since ${since.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  return `
    <article class="note note--order account__note">
      <span class="note__staple" aria-hidden="true"></span>
      <p class="note__kicker">Status · Registered runner</p>
      <h2 class="note__title">${escapeHtml(user.email ?? user.displayName ?? 'Runner')}</h2>
      <p class="note__body">Your progress is stored in this account${sinceLabel}. Sign in with the same email on any device to continue.</p>
    </article>
    <div class="account__signout">
      <p class="form__hint">Signing out removes this device's copy of your runs. Your account keeps everything.</p>
      <p class="form__error" data-role="error" role="alert" hidden></p>
      <div class="brief__actions">
        <button type="button" class="btn btn--ghost" data-action="signout">Sign out</button>
      </div>
    </div>
  `;
}

function offlineMarkup(): string {
  return `
    <article class="note note--order account__note">
      <span class="note__staple" aria-hidden="true"></span>
      <p class="note__kicker">Status · Offline</p>
      <h2 class="note__title">No line to the server</h2>
      <p class="note__body">
        Accounts need a connection. Your runs stay on this device for now and are sent over as
        soon as the server can be reached again.
      </p>
    </article>
  `;
}

export function accountMarkup(view: AccountView): string {
  const body = !view.online
    ? offlineMarkup()
    : view.user === null || view.user.isGuest
      ? guestMarkup()
      : signedInMarkup(view.user);
  return `
    <div class="screen screen--brief account">
      <header class="sub-header">
        <button class="btn btn--ghost btn--icon" data-action="back" aria-label="Back to the map">←</button>
        <div>
          <h1>Runner's Papers</h1>
          <p>Keep your progress across devices</p>
        </div>
      </header>
      ${body}
      <p class="form__hint account__kit">Name and sound live in the <a href="#/settings">runner's kit</a>.</p>
    </div>
  `;
}

export interface Credentials {
  readonly email: string;
  readonly password: string;
}

/** Client-side check mirroring the server's rules, so obvious mistakes never leave the device. */
export function readCredentials(form: HTMLFormElement): Credentials | string {
  const data = new FormData(form);
  const email = String(data.get('email') ?? '').trim();
  const password = String(data.get('password') ?? '');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Enter a valid email address.';
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `The password needs at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  return { email, password };
}

export function showFormError(container: ParentNode, message: string | null): void {
  const el = container.querySelector<HTMLElement>('[data-role="error"]');
  if (!el) return;
  el.textContent = message ?? '';
  el.hidden = message === null;
}

export function setFormBusy(form: HTMLFormElement, busy: boolean): void {
  form.querySelectorAll<HTMLButtonElement | HTMLInputElement>('button, input').forEach((el) => {
    el.disabled = busy;
  });
}
